import { useState, useCallback } from 'react';

/**
 * Custom hook for managing preflight recovery state
 */
export const useRecoveryState = ({ onComponentStateChange }) => {
    const [recoveryStates, setRecoveryStates] = useState(new Map());

    // Handle error/warning recovery (matches Backbone handleErrorWarningRecovery)
    const handleErrorWarningRecovery = useCallback(
        (componentId, checkId, errorIndex, recoveryChoice) => {
            const recoveryKey = `${componentId}-${checkId}-${errorIndex}`;

            setRecoveryStates((prev) => {
                const newStates = new Map(prev);
                if (recoveryChoice === 'recover') {
                    newStates.set(recoveryKey, {
                        migration_instruction: 'override_source_migration=true',
                    });
                } else {
                    newStates.delete(recoveryKey);
                }
                return newStates;
            });

            // Notify parent component of state change
            if (onComponentStateChange) {
                onComponentStateChange(componentId, {
                    errorType: recoveryChoice === 'recover' ? 'warning' : 'fatal',
                    recovery:
                        recoveryChoice === 'recover'
                            ? [{ migration_instruction: 'override_source_migration=true' }]
                            : [],
                    selected: recoveryChoice === 'recover',
                });
            }
        },
        [onComponentStateChange],
    );

    // Auto-recovery processing (matches Backbone auto-recovery logic)
    const processAutoRecovery = useCallback(
        (componentId, data) => {
            if (!data?.content) return;

            // Look for auto-recovery elements
            data.content.forEach((check) => {
                if (check.errors) {
                    check.errors.forEach((error, errorIndex) => {
                        if (error.recovery === 'source_has_previous_migration_attempt_recovery') {
                            // This is an auto-recovery case - automatically enable recovery
                            handleErrorWarningRecovery(componentId, check.id, errorIndex, 'recover');
                        }
                    });
                }
            });
        },
        [handleErrorWarningRecovery],
    );

    // Get recovery state for a specific key
    const getRecoveryState = useCallback(
        (componentId, checkId, errorIndex) => {
            const recoveryKey = `${componentId}-${checkId}-${errorIndex}`;
            return recoveryStates.get(recoveryKey);
        },
        [recoveryStates],
    );

    // Clear all recovery states
    const clearRecoveryStates = useCallback(() => {
        setRecoveryStates(new Map());
    }, []);

    return {
        recoveryStates,
        handleErrorWarningRecovery,
        processAutoRecovery,
        getRecoveryState,
        clearRecoveryStates,
    };
};
